import { jsPDF } from "jspdf";
// Als data-URL geïmporteerd zodat jsPDF de bytes direct krijgt en niet van het
// bestandssysteem hoeft te lezen (dat faalt buiten de browser en zou de brief
// zonder briefpapier laten ontstaan).
import helloLogo from "@/assets/hello_logo.png?inline";

/**
 * Vast HelloHypotheek-briefpapier voor een eenvoudige, platte-tekst brief.
 * Genereert een A4 PDF met logo, datum, marges, regelafbreking en meerdere
 * pagina's met paginanummers. Geen rich text, mergevelden of per-kantoor
 * briefpapier — dat valt bewust buiten scope (SPEC-026).
 */

const PAGE_WIDTH = 210;
const PAGE_HEIGHT = 297;
const MARGIN_LEFT = 25;
const MARGIN_RIGHT = 25;
const MARGIN_TOP = 20;
const MARGIN_BOTTOM = 20;
const LOGO_SIZE = 40;
const FONT_SIZE = 11;
const LINE_HEIGHT = 6;

const CONTENT_WIDTH = PAGE_WIDTH - MARGIN_LEFT - MARGIN_RIGHT;

/**
 * Datum in het formaat YYYY-MM-DD op basis van de lokale kalenderdatum.
 * Bewust niet toISOString() (UTC): rond lokale middernacht zou dat de dag ervoor
 * kunnen opleveren, waardoor de brief en de bestandsnaam de verkeerde datum krijgen.
 */
export function letterDateStamp(now: Date): string {
	const year = now.getFullYear();
	const month = String(now.getMonth() + 1).padStart(2, "0");
	const day = String(now.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
}

/** Bestandsnaam voor de gegenereerde brief. */
export function letterFileName(now: Date): string {
	return `brief-${letterDateStamp(now)}.pdf`;
}

function addLogo(doc: jsPDF): void {
	// Het logo hoort bij het vaste briefpapier. Kan een render laag falen
	// (bv. omdat de omgeving geen afbeeldingen ondersteunt), dan mag de brief
	// niet stilletjes zonder briefpapier ontstaan: de fout propageert naar de
	// aanroeper zodat de tekst blijft staan en er niet gekoppeld wordt.
	doc.addImage(
		helloLogo,
		"PNG",
		PAGE_WIDTH / 2 - LOGO_SIZE / 2,
		MARGIN_TOP,
		LOGO_SIZE,
		LOGO_SIZE,
	);
}

function addPageNumbers(doc: jsPDF): void {
	const pageCount = doc.getNumberOfPages();
	if (pageCount <= 1) {
		return;
	}
	for (let page = 1; page <= pageCount; page++) {
		doc.setPage(page);
		doc.setFontSize(9);
		doc.text(
			`${page} / ${pageCount}`,
			PAGE_WIDTH - MARGIN_RIGHT,
			PAGE_HEIGHT - MARGIN_BOTTOM / 2,
			{ align: "right" },
		);
	}
}

/**
 * Bouwt de PDF voor de gegeven platte tekst. Gooit als de tekst leeg is of als
 * de render faalt; in beide gevallen mag er geen bestand ontstaan.
 */
export function buildLetterPdf(text: string, now: Date): jsPDF {
	if (text.trim().length === 0) {
		throw new Error("Lege brieftekst kan niet worden gegenereerd");
	}

	const doc = new jsPDF("p", "mm", "a4");
	addLogo(doc);

	doc.setFontSize(FONT_SIZE);
	doc.text(letterDateStamp(now), MARGIN_LEFT, MARGIN_TOP + LOGO_SIZE + 15);

	let cursorY = MARGIN_TOP + LOGO_SIZE + 30;
	// splitTextToSize verzorgt de regelafbreking binnen de tekstbreedte;
	// expliciete regeleindes in de invoer blijven behouden.
	const lines = doc.splitTextToSize(text, CONTENT_WIDTH) as string[];

	for (const line of lines) {
		if (cursorY > PAGE_HEIGHT - MARGIN_BOTTOM) {
			doc.addPage();
			cursorY = MARGIN_TOP;
		}
		doc.text(line, MARGIN_LEFT, cursorY);
		cursorY += LINE_HEIGHT;
	}

	addPageNumbers(doc);
	return doc;
}

/**
 * Genereert de brief als een PDF-File met naam brief-YYYY-MM-DD.pdf en
 * MIME-type application/pdf. Gooit bij lege tekst of een renderfout.
 */
export function generateLetterPdf(text: string, now: Date = new Date()): File {
	const doc = buildLetterPdf(text, now);
	const blob = doc.output("blob") as Blob;
	return new File([blob], letterFileName(now), {
		type: "application/pdf",
	});
}
